import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  AtSign,
  Check,
  FileText,
  Hash,
  Mail,
  MessageCircle,
  RefreshCw,
  Save,
  Tag,
  ThumbsUp,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { ChannelTextCard } from "@/components/ChannelTextCard";
import { Toast, type ToastKind } from "@/components/Toast";
import { StatusBadge } from "@/components/StatusBadge";
import { api, type ChannelTexts } from "@/lib/api";
import { useTournament } from "@/hooks/useTournaments";
import {
  useApproveTexts,
  useRunStep,
  useTexts,
} from "@/hooks/usePipeline";

const EMPTY: ChannelTexts = {
  instagram_caption: "",
  facebook_post: "",
  whatsapp_message: "",
  email_subject: "",
  email_preheader: "",
  landing_hero_title: "",
  landing_hero_subtitle: "",
  landing_body: "",
  playtomic_titles: {},
  hashtags: [],
};

export default function CheckpointTexts() {
  const { slug } = useParams<{ slug: string }>();
  const qc = useQueryClient();

  const tournament = useTournament(slug);
  const texts = useTexts(slug);
  const regenerate = useRunStep(slug ?? "");
  const approve = useApproveTexts(slug ?? "");

  const [draft, setDraft] = useState<ChannelTexts>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(null);

  // Quando i testi arrivano dall'API, inizializza il draft
  useEffect(() => {
    if (texts.data) {
      setDraft({ ...EMPTY, ...texts.data });
    }
  }, [texts.data]);

  if (!slug) return <div className="text-sm text-slate-500">Slug mancante.</div>;

  const tName = (tournament.data?.input.tournament?.name as string) || slug;
  const status = (tournament.data?.state?.status as string) || "DRAFT";
  const textsMissing = texts.isError && (texts.error as { status?: number } | null)?.status === 404;

  const dirtyFields = texts.data
    ? {
        instagram_caption: draft.instagram_caption !== texts.data.instagram_caption,
        facebook_post: draft.facebook_post !== texts.data.facebook_post,
        whatsapp_message: draft.whatsapp_message !== texts.data.whatsapp_message,
        email_subject: draft.email_subject !== texts.data.email_subject,
        email_preheader: draft.email_preheader !== texts.data.email_preheader,
        landing_body: draft.landing_body !== texts.data.landing_body,
      }
    : {};
  const anyDirty = Object.values(dirtyFields).some(Boolean);

  const update =
    <K extends keyof ChannelTexts>(key: K) =>
    (value: ChannelTexts[K]) =>
      setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.putTexts(slug, draft);
      qc.invalidateQueries({ queryKey: ["texts", slug] });
      // Refetch stato torneo: se l'email era gia composta (step 5 completato),
      // il backend ha invalidato email.json e resettato step 5 → pending.
      qc.invalidateQueries({ queryKey: ["tournament", slug] });
      if (res?.email_invalidated) {
        setToast({
          kind: "success",
          message:
            "Testi salvati ✓ — email da ricomporre (step 5) prima di inviare",
        });
      } else {
        setToast({ kind: "success", message: "Testi salvati ✓" });
      }
    } catch (e) {
      setToast({ kind: "error", message: `Errore salvataggio: ${String(e)}` });
    } finally {
      setSaving(false);
    }
  };

  const regen = () => {
    regenerate.mutate(
      { step: 1 },
      {
        onSuccess: () => {
          setToast({
            kind: "success",
            message: "Testi rigenerati: rileggo dall'API",
          });
          qc.invalidateQueries({ queryKey: ["texts", slug] });
        },
        onError: (e) =>
          setToast({ kind: "error", message: `Errore: ${String(e)}` }),
      },
    );
  };

  const doApprove = () => {
    if (anyDirty) {
      setToast({
        kind: "error",
        message: "Salva le modifiche prima di approvare",
      });
      return;
    }
    approve.mutate(undefined, {
      onSuccess: () =>
        setToast({
          kind: "success",
          message: "Testi approvati — puoi passare allo step 3 (locandina)",
        }),
      onError: (e) => setToast({ kind: "error", message: `Errore: ${String(e)}` }),
    });
  };

  const hashtagsStr = (draft.hashtags ?? []).join(" ");
  const onHashtagsChange = (v: string) => {
    const list = v
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.startsWith("#") ? t : "#" + t));
    setDraft((d) => ({ ...d, hashtags: list }));
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Back */}
      <div>
        <Link
          to={`/tournaments/${slug}`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {tName}
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <FileText className="h-6 w-6 text-moving-500" />
            Checkpoint testi
          </h1>
          <p className="text-sm text-slate-500">
            Rivedi, correggi ed approva i testi per ciascun canale.
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Loading / empty state */}
      {texts.isLoading && !textsMissing ? (
        <div className="text-sm text-slate-500">Caricamento testi…</div>
      ) : null}

      {textsMissing ? (
        <Card>
          <CardBody className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
            <div>
              <div className="text-sm font-medium text-slate-700">
                Testi non ancora generati
              </div>
              <div className="text-xs text-slate-500">
                Esegui lo step 1 per generarli automaticamente.
              </div>
            </div>
            <Button onClick={regen} loading={regenerate.isPending}>
              <RefreshCw className="h-4 w-4" />
              Genera testi (step 1)
            </Button>
          </CardBody>
        </Card>
      ) : texts.data ? (
        <>
          {/* Toolbar azioni */}
          <Card>
            <CardBody className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                {anyDirty ? (
                  <span className="flex items-center gap-1.5 text-amber-700">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    Modifiche non salvate
                  </span>
                ) : (
                  <span className="text-slate-500">Nessuna modifica in corso</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={regen}
                  loading={regenerate.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                  Rigenera con AI
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={save}
                  loading={saving}
                  disabled={!anyDirty || saving}
                >
                  <Save className="h-4 w-4" />
                  Salva modifiche
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={doApprove}
                  loading={approve.isPending}
                  disabled={anyDirty || approve.isPending}
                >
                  <Check className="h-4 w-4" />
                  Approva testi
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* 4 canali */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <ChannelTextCard
              icon={<AtSign className="h-4 w-4" />}
              channel="Instagram caption"
              hint="feed post — max 2200 caratteri"
              value={draft.instagram_caption}
              onChange={update("instagram_caption")}
              softLimit={1500}
              hardLimit={2200}
              rows={10}
              dirty={dirtyFields.instagram_caption}
            />
            <ChannelTextCard
              icon={<ThumbsUp className="h-4 w-4" />}
              channel="Facebook post"
              hint="feed post"
              value={draft.facebook_post}
              onChange={update("facebook_post")}
              softLimit={800}
              rows={10}
              dirty={dirtyFields.facebook_post}
            />
            <ChannelTextCard
              icon={<MessageCircle className="h-4 w-4" />}
              channel="WhatsApp message"
              hint="{first_name} = nome del destinatario, STOP disclaimer aggiunto automaticamente"
              value={draft.whatsapp_message}
              onChange={update("whatsapp_message")}
              softLimit={700}
              rows={10}
              dirty={dirtyFields.whatsapp_message}
            />
            <div className="flex flex-col gap-4">
              <ChannelTextCard
                icon={<Mail className="h-4 w-4" />}
                channel="Email subject"
                hint="oggetto visibile in inbox"
                value={draft.email_subject}
                onChange={update("email_subject")}
                softLimit={50}
                hardLimit={100}
                rows={2}
                dirty={dirtyFields.email_subject}
              />
              <ChannelTextCard
                icon={<Mail className="h-4 w-4" />}
                channel="Email preheader"
                hint="anteprima inbox (dopo il subject)"
                value={draft.email_preheader}
                onChange={update("email_preheader")}
                softLimit={90}
                hardLimit={150}
                rows={2}
                dirty={dirtyFields.email_preheader}
              />
            </div>
          </div>

          {/* Hashtags */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Hashtag (Instagram/Facebook)
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              <Label htmlFor="hashtags">
                Separati da spazi (il cancelletto viene aggiunto automaticamente)
              </Label>
              <Input
                id="hashtags"
                value={hashtagsStr}
                onChange={(e) => onHashtagsChange(e.target.value)}
                placeholder="#torneo #padel #monza"
              />
              <div className="flex flex-wrap gap-1">
                {(draft.hashtags ?? []).map((h) => (
                  <span
                    key={h}
                    className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                  >
                    {h}
                  </span>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Playtomic titles */}
          {draft.playtomic_titles && Object.keys(draft.playtomic_titles).length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Titoli Playtomic (per categoria)
                </CardTitle>
              </CardHeader>
              <CardBody className="space-y-2">
                {Object.entries(draft.playtomic_titles).map(([cat, title]) => (
                  <div key={cat} className="grid grid-cols-[120px_1fr] gap-2 items-center">
                    <span className="text-xs font-medium text-slate-500">{cat}</span>
                    <Input
                      value={title}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          playtomic_titles: {
                            ...d.playtomic_titles,
                            [cat]: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}
        </>
      ) : null}

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}
